import { useEffect, lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClientProvider } from "@tanstack/react-query";
import { HelmetProvider } from "react-helmet-async";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { NavigationSelector } from "./components/navigation/index";
import { FooterSelector } from "./components/footer/index";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { siteConfig } from "@/lib/site-config";
import { useSiteConfig } from "@/lib/useSiteConfig";
import ProtectedRoute from "@/components/auth/ProtectedRoute";
import { PageRenderer } from "@/components/pages/PageRenderer";
import { useSiteSettings } from "@/hooks/useSiteSettings";
import { ThemeProvider } from "@/lib/theme-provider";
import { LayoutProvider, useLayout } from "@/lib/layout-provider";
import { cn } from "@/lib/utils";
import { queryClient } from "@/lib/query-config";
import { GoogleAnalytics } from "@/components/GoogleAnalytics";
// Public pages - loaded immediately
import Index from "./pages/Index";
import Contact from "./pages/Contact";
import News from "./pages/News";
import Events from "./pages/Events";
import Gallery from "./pages/Gallery";
import Projects from "./pages/Projects";
import Search from "./pages/Search";
import Form from "./pages/Form";
import Membership from "./pages/Membership";
import Donate from "./pages/Donate";
import NotFound from "./pages/NotFound";

// Helper function for lazy loading with retry logic
const lazyWithRetry = (componentImport: () => Promise<any>, retries = 3) => {
  return lazy(async () => {
    for (let i = 0; i < retries; i++) {
      try {
        return await componentImport();
      } catch (error) {
        if (i === retries - 1) {
          console.error('Failed to load component after retries:', error);
          throw error;
        }
        // Wait before retrying (exponential backoff)
        await new Promise(resolve => setTimeout(resolve, 1000 * Math.pow(2, i)));
      }
    }
    throw new Error('Failed to load component');
  });
};

// Admin pages - lazy loaded for better performance
const Login = lazyWithRetry(() => import("./pages/admin/Login"));
const Dashboard = lazyWithRetry(() => import("./pages/admin/Dashboard"));
const DashboardHome = lazyWithRetry(() => import("./pages/admin/DashboardHome"));
const Settings = lazyWithRetry(() => import("./pages/admin/settings/Settings"));
const LayoutSettings = lazyWithRetry(() => import("./pages/admin/settings/LayoutSettings"));
const OrganizationDetails = lazyWithRetry(() => import("./pages/admin/settings/OrganizationDetails"));
const PagesList = lazy(() => import("./pages/admin/pages/PagesList").then(m => ({ default: m.PagesList })));
const EditPage = lazy(() => import("./pages/admin/pages/EditPage").then(m => ({ default: m.EditPage })));
const EventsManagement = lazy(() => import("./pages/admin/events/EventsManagement").then(m => ({ default: m.EventsManagement })));
const NewsManagement = lazy(() => import("./pages/admin/news/NewsManagement").then(m => ({ default: m.NewsManagement })));
const TestimonialsManagement = lazy(() => import("./pages/admin/testimonials/TestimonialsManagement").then(m => ({ default: m.TestimonialsManagement })));
const PhotoAlbumsManagement = lazy(() => import("./pages/admin/gallery/PhotoAlbumsManagement"));
const PhotoUpload = lazy(() => import("./pages/admin/gallery/PhotoUpload"));
const ProjectsManagement = lazy(() => import("./pages/admin/projects/ProjectsManagement"));
const SponsorsManagement = lazy(() => import("./pages/admin/sponsors/SponsorsManagement"));
const PartnersManagement = lazy(() => import("./pages/admin/partners/PartnersManagement"));
const FactsManagement = lazy(() => import("./pages/admin/facts/FactsManagement"));
const NewsletterDesigner = lazy(() => import("./pages/admin/newsletters/NewsletterDesigner"));
const TilesManagement = lazy(() => import("./pages/admin/homepage/TilesManagement"));
const ImpactStatsManagement = lazy(() => import("./pages/admin/homepage/ImpactStatsManagement"));
const HeroImagesManagement = lazy(() => import("./pages/admin/homepage/HeroImagesManagement"));
const ContactSubmissions = lazy(() => import("./pages/admin/contact/ContactSubmissions"));
const DonationsManagement = lazy(() => import("./pages/admin/donations/DonationsManagement"));
const MembershipManagement = lazy(() => import("./pages/admin/membership/MembershipManagement"));
const EventRegistrations = lazy(() => import("./pages/admin/events/EventRegistrations"));
const FormsList = lazy(() => import("./pages/admin/forms/FormsList"));
const FormBuilder = lazy(() => import("./pages/admin/forms/FormBuilder"));
const FormSubmissions = lazy(() => import("./pages/admin/forms/FormSubmissions"));
const QuoteRequests = lazy(() => import("./pages/admin/quotes/QuoteRequests"));
const AnalyticsDashboard = lazy(() => import("./pages/admin/analytics/AnalyticsDashboard"));
const SEOManagement = lazy(() => import("./pages/admin/seo/SEOManagement"));
const RolesManagement = lazy(() => import("./pages/admin/roles/RolesManagement"));
const PermissionsManagement = lazy(() => import("./pages/admin/roles/PermissionsManagement"));
const SearchAnalytics = lazy(() => import("./pages/admin/search/SearchAnalytics"));

// Import reusable loading component
import { PageLoader } from "@/components/ui/loading";

const AppRoutesContent = () => {
  const { data: siteSettings, features, isLoading: settingsLoading } = useSiteSettings();
  const { currentLayout } = useLayout();
  const effectiveConfig = useSiteConfig();
  const location = useLocation();
  const isInitialSettingsHydration = settingsLoading && typeof siteSettings === "undefined";
  
  // Check if we're on an admin route
  const isAdminRoute = location.pathname.startsWith('/admin');
  
  if (isInitialSettingsHydration && !isAdminRoute) {
    return <PageLoader message="Loading your website settings..." />;
  }
  
  // Check if we're using a sidebar navigation that needs content offset
  const hasSidebarNav = currentLayout.navigation.style === 'sidebar-left';
  const shouldApplyPublicSidebarOffset = !isAdminRoute && hasSidebarNav;

  // Set dynamic document title from site config (uses database values if available)
  useEffect(() => {
    document.title = `${effectiveConfig.organizationName} - ${effectiveConfig.tagline}`;
  }, [effectiveConfig]);

  return (
    <div className="min-h-screen flex flex-col">
      {!isAdminRoute && <NavigationSelector />}
      <main className={cn("flex-1", shouldApplyPublicSidebarOffset && "ml-64")}>
          <Routes>
            <Route path="/" element={<Index />} />
            <Route path="/about" element={<PageRenderer customSlug="about" />} />
            <Route path="/about-us" element={<PageRenderer customSlug="about" />} />
            <Route path="/services" element={<PageRenderer customSlug="services" />} />
            {features.programs.enabled && (
              <Route path="/programs" element={<PageRenderer customSlug="programs" />} />
            )}
            <Route path="/contact" element={<Contact />} />
            <Route path="/search" element={<Search />} />
            <Route path="/form/:slug" element={<Form />} />

            {/* Conditional routes based on feature flags */}
            {features.news.enabled && (
              <Route path="/news" element={<News />} />
            )}
            {features.events.enabled && (
              <Route path="/events" element={<Events />} />
            )}
            {features.membership.enabled && (
              <Route path="/membership" element={<Membership />} />
            )}
            {features.donation.enabled && (
              <Route path="/donate" element={<Donate />} />
            )}
            {features.gallery.enabled && (
              <>
                <Route path="/gallery" element={<Gallery />} />
                <Route path="/gallery/:albumId" element={<Gallery />} />
              </>
            )}
            <Route path="/projects" element={<Projects />} />

            {/* Static pages */}
            <Route path="/privacy" element={<PageRenderer customSlug="privacy" />} />
            <Route path="/accessibility" element={<PageRenderer customSlug="accessibility" />} />

            {/* Redirect disabled features to home */}
            {!features.news.enabled && (
              <Route path="/news" element={<Navigate to="/" replace />} />
            )}
            {!features.events.enabled && (
              <Route path="/events" element={<Navigate to="/" replace />} />
            )}
            {!features.membership.enabled && (
              <Route path="/membership" element={<Navigate to="/" replace />} />
            )}
            {!features.donation.enabled && (
              <Route path="/donate" element={<Navigate to="/" replace />} />
            )}
            {!features.gallery.enabled && (
              <>
                <Route path="/gallery" element={<Navigate to="/" replace />} />
                <Route path="/gallery/:albumId" element={<Navigate to="/" replace />} />
              </>
            )}
            {!features.programs.enabled && (
              <Route path="/programs" element={<Navigate to="/" replace />} />
            )}

            {/* Admin Routes - Lazy loaded for performance */}
            <Route path="/admin" element={<Navigate to="/admin/dashboard" replace />} />
            <Route 
              path="/admin/login" 
              element={
                <Suspense fallback={<PageLoader />}>
                  <Login />
                </Suspense>
              } 
            />

            <Route
              path="/admin/dashboard/*"
              element={
                <ProtectedRoute>
                  <Suspense fallback={<PageLoader />}>
                    <Dashboard />
                  </Suspense>
                </ProtectedRoute>
              }
            >
              <Route 
                index 
                element={
                  <Suspense fallback={<PageLoader />}>
                    <DashboardHome />
                  </Suspense>
                } 
              />

              {/* Settings */}
              <Route 
                path="settings" 
                element={
                  <Suspense fallback={<PageLoader />}>
                    <Settings />
                  </Suspense>
                } 
              />
              <Route 
                path="settings/layout" 
                element={
                  <Suspense fallback={<PageLoader />}>
                    <LayoutSettings />
                  </Suspense>
                } 
              />
              <Route 
                path="settings/organization" 
                element={
                  <Suspense fallback={<PageLoader />}>
                    <OrganizationDetails />
                  </Suspense>
                } 
              />
              <Route 
                path="settings/roles" 
                element={
                  <Suspense fallback={<PageLoader />}>
                    <RolesManagement />
                  </Suspense>
                } 
              />
              <Route 
                path="settings/permissions" 
                element={
                  <Suspense fallback={<PageLoader />}>
                    <PermissionsManagement />
                  </Suspense>
                } 
              />

              {/* Homepage Management */}
              <Route 
                path="homepage/tiles" 
                element={
                  <Suspense fallback={<PageLoader />}>
                    <TilesManagement />
                  </Suspense>
                } 
              />
                      <Route 
                        path="homepage/stats" 
                        element={
                          <Suspense fallback={<PageLoader />}>
                            <ImpactStatsManagement />
                          </Suspense>
                        } 
                      />
                      <Route 
                        path="homepage/hero-images" 
                        element={
                          <Suspense fallback={<PageLoader />}>
                            <HeroImagesManagement />
                          </Suspense>
                        } 
                      />

              {/* Content Management */}
              <Route 
                path="events" 
                element={
                  <Suspense fallback={<PageLoader />}>
                    <EventsManagement />
                  </Suspense>
                } 
              />
              <Route 
                path="events/:eventId/registrations" 
                element={
                  <Suspense fallback={<PageLoader />}>
                    <EventRegistrations />
                  </Suspense>
                } 
              />
              <Route 
                path="contact/submissions" 
                element={
                  <Suspense fallback={<PageLoader />}>
                    <ContactSubmissions />
                  </Suspense>
                } 
              />
              <Route 
                path="donations" 
                element={
                  <Suspense fallback={<PageLoader />}>
                    <DonationsManagement />
                  </Suspense>
                } 
              />
              <Route 
                path="membership" 
                element={
                  <Suspense fallback={<PageLoader />}>
                    <MembershipManagement />
                  </Suspense>
                } 
              />
              <Route 
                path="quotes" 
                element={
                  <Suspense fallback={<PageLoader />}>
                    <QuoteRequests />
                  </Suspense>
                } 
              />
              <Route 
                path="forms" 
                element={
                  <Suspense fallback={<PageLoader />}>
                    <FormsList />
                  </Suspense>
                } 
              />
              <Route 
                path="forms/new" 
                element={
                  <Suspense fallback={<PageLoader />}>
                    <FormBuilder />
                  </Suspense>
                } 
              />
              <Route 
                path="forms/:id" 
                element={
                  <Suspense fallback={<PageLoader />}>
                    <FormBuilder />
                  </Suspense>
                } 
              />
              <Route 
                path="forms/:id/submissions" 
                element={
                  <Suspense fallback={<PageLoader />}>
                    <FormSubmissions />
                  </Suspense>
                } 
              />
              <Route 
                path="news" 
                element={
                  <Suspense fallback={<PageLoader />}>
                    <NewsManagement />
                  </Suspense>
                } 
              />
              <Route 
                path="testimonials" 
                element={
                  <Suspense fallback={<PageLoader />}>
                    <TestimonialsManagement />
                  </Suspense>
                } 
              />
              <Route 
                path="facts" 
                element={
                  <Suspense fallback={<PageLoader />}>
                    <FactsManagement />
                  </Suspense>
                } 
              />

              {/* Gallery Management */}
              <Route 
                path="gallery" 
                element={
                  <Suspense fallback={<PageLoader />}>
                    <PhotoAlbumsManagement />
                  </Suspense>
                } 
              />
              <Route 
                path="projects" 
                element={
                  <Suspense fallback={<PageLoader />}>
                    <ProjectsManagement />
                  </Suspense>
                } 
              />
              <Route 
                path="gallery/:albumId/upload" 
                element={
                  <Suspense fallback={<PageLoader />}>
                    <PhotoUpload />
                  </Suspense>
                } 
              />

              {/* Sponsors & Partners */}
              <Route 
                path="sponsors" 
                element={
                  <Suspense fallback={<PageLoader />}>
                    <SponsorsManagement />
                  </Suspense>
                } 
              />
              <Route 
                path="partners" 
                element={
                  <Suspense fallback={<PageLoader />}>
                    <PartnersManagement />
                  </Suspense>
                } 
              />

              {/* Page Management */}
              <Route 
                path="pages" 
                element={
                  <Suspense fallback={<PageLoader />}>
                    <PagesList />
                  </Suspense>
                } 
              />
              <Route 
                path="pages/new" 
                element={
                  <Suspense fallback={<PageLoader />}>
                    <EditPage />
                  </Suspense>
                } 
              />
              <Route 
                path="pages/edit/:slug" 
                element={
                  <Suspense fallback={<PageLoader />}>
                    <EditPage />
                  </Suspense>
                } 
              />

              {/* Newsletter Designer */}
              <Route 
                path="newsletters" 
                element={
                  <Suspense fallback={<PageLoader />}>
                    <NewsletterDesigner />
                  </Suspense>
                } 
              />

              {/* Analytics Dashboard */}
              <Route 
                path="analytics" 
                element={
                  <Suspense fallback={<PageLoader />}>
                    <AnalyticsDashboard />
                  </Suspense>
                } 
              />
              <Route 
                path="search/analytics" 
                element={
                  <Suspense fallback={<PageLoader />}>
                    <SearchAnalytics />
                  </Suspense>
                } 
              />

              {/* SEO Management */}
              <Route 
                path="seo" 
                element={
                  <Suspense fallback={<PageLoader />}>
                    <SEOManagement />
                  </Suspense>
                } 
              />
            </Route>

            {/* Dynamic Pages - Catch-all for CMS pages */}
            {/* This route handles all pages from the database */}
            {/* Excluded paths are handled by specific routes above */}
            <Route
              path="/:slug"
              element={<PageRenderer />}
            />

            {/* 404 Route */}
            <Route path="*" element={<NotFound />} />
            {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
          </Routes>
        </main>
        {!isAdminRoute && (
          <div className={cn(shouldApplyPublicSidebarOffset && "ml-64")}>
            <FooterSelector />
          </div>
        )}
      </div>
    );
  };

const AppRoutes = () => {
  return (
    <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
      <GoogleAnalytics />
      <AppRoutesContent />
    </BrowserRouter>
  );
};

const App = () => (
  <ErrorBoundary>
    <HelmetProvider>
      <QueryClientProvider client={queryClient}>
        <TooltipProvider>
          <ThemeProvider>
            <LayoutProvider>
              <Toaster />
              <Sonner />
              <AppRoutes />
            </LayoutProvider>
          </ThemeProvider>
        </TooltipProvider>
      </QueryClientProvider>
    </HelmetProvider>
  </ErrorBoundary>
);

export default App;
